/**
 * Scheduled maintenance tasks (FORGE-179) — portal mirror of Forge's
 * src/orchestrator/shared/schedule.ts. Pure config + cron logic; keep in sync so
 * the Schedules editor blocks anything the scanner would reject. Cron is a
 * standard 5-field expression evaluated in UTC.
 */

export type TaskType = 'security-review' | 'performance' | 'custom' | 'drift';
export const TASK_TYPES: readonly TaskType[] = ['security-review', 'performance', 'custom', 'drift'];

/**
 * FORGE-332 — MIRROR of Forge `src/orchestrator/shared/schedule.ts`. These two
 * files must stay in lockstep: the portal validates on save, the orchestrator
 * validates at run time, and a divergence means a schedule that saves cleanly
 * and is then silently rejected by the runner.
 *
 * `drift` differs from the other three in what a valid schedule looks like:
 * they carry a PROMPT and let an agent use judgment, while a drift schedule
 * carries a PROBE LIST and none. So `prompt` is optional for drift, `probes` is
 * required, and probes are rejected on every other type.
 *
 * Probe ids have ONE source of truth — Forge `agent/scripts/lib/drift/registry.js`
 * — supplied here through `opts.knownProbes`. Do NOT hardcode a probe list in
 * this file: that is the trap from the llm-provider-registration note, where a
 * new entry type-checks green and is rejected at runtime because one of ~20
 * mirrored allow-lists was missed.
 */
export const DRIFT_TASK_TYPE: TaskType = 'drift';

export const SCHEDULE_PROVIDERS = ['anthropic', 'bedrock', 'openai', 'vertex', 'meta', 'kimi'] as const;
export type ScheduleProvider = (typeof SCHEDULE_PROVIDERS)[number];

export interface ScheduleConfig {
  id?: string;
  name: string;
  task_type: TaskType;
  prompt: string;
  repos: string[];
  cron: string;
  enabled: boolean;
  /** Optional per-schedule LLM override (provider is plan-gated). */
  provider?: string;
  model?: string;
  /** FORGE-332 — drift schedules only: probe ids to run. */
  probes?: string[];
}

export const MIN_PROMPT_LEN = 10;
export const MAX_PROMPT_LEN = 8000;

interface Field { any: boolean; vals: Set<number>; }

function parseField(raw: string, min: number, max: number): Field {
  const f: Field = { any: false, vals: new Set() };
  for (const part of raw.split(',')) {
    const [rangePart, stepPart] = part.split('/');
    const step = stepPart ? Number(stepPart) : 1;
    if (stepPart !== undefined && (!Number.isInteger(step) || step < 1)) {
      throw new Error(`invalid step "${part}"`);
    }
    let lo: number, hi: number;
    if (rangePart === '*') { lo = min; hi = max; if (stepPart === undefined) f.any = true; }
    else if (rangePart.includes('-')) {
      const [a, b] = rangePart.split('-').map(Number);
      if (!Number.isInteger(a) || !Number.isInteger(b)) throw new Error(`invalid range "${part}"`);
      lo = a; hi = b;
    } else {
      const v = Number(rangePart);
      if (!Number.isInteger(v)) throw new Error(`invalid value "${part}"`);
      lo = v; hi = v;
    }
    if (lo < min || hi > max || lo > hi) throw new Error(`out-of-range "${part}" (expected ${min}-${max})`);
    for (let v = lo; v <= hi; v += step) f.vals.add(v);
  }
  return f;
}

interface ParsedCron { minute: Field; hour: Field; dom: Field; month: Field; dow: Field; }

export function parseCron(expr: string): ParsedCron {
  const parts = String(expr).trim().split(/\s+/);
  if (parts.length !== 5) throw new Error(`cron must have 5 fields (got ${parts.length})`);
  const [mi, ho, dm, mo, dw] = parts;
  const dowField = parseField(dw, 0, 7);
  if (dowField.vals.has(7)) { dowField.vals.add(0); dowField.vals.delete(7); }
  return {
    minute: parseField(mi, 0, 59),
    hour:   parseField(ho, 0, 23),
    dom:    parseField(dm, 1, 31),
    month:  parseField(mo, 1, 12),
    dow:    dowField,
  };
}

export function isValidCron(expr: string): boolean {
  try { parseCron(expr); return true; } catch { return false; }
}

function matchesAt(c: ParsedCron, d: Date): boolean {
  const minOk   = c.minute.any || c.minute.vals.has(d.getUTCMinutes());
  const hourOk  = c.hour.any   || c.hour.vals.has(d.getUTCHours());
  const monthOk = c.month.any  || c.month.vals.has(d.getUTCMonth() + 1);
  if (!minOk || !hourOk || !monthOk) return false;
  const domOk = c.dom.any || c.dom.vals.has(d.getUTCDate());
  const dowOk = c.dow.any || c.dow.vals.has(d.getUTCDay());
  if (c.dom.any && c.dow.any) return true;
  if (!c.dom.any && !c.dow.any) return domOk || dowOk;
  return domOk && dowOk;
}

const MINUTE_MS = 60_000;

export function isDue(
  cron: string,
  lastRunAtMs: number | null | undefined,
  nowMs: number,
  opts: { maxLookbackMs?: number } = {},
): boolean {
  let c: ParsedCron;
  try { c = parseCron(cron); } catch { return false; }
  const maxLookback = opts.maxLookbackMs ?? 26 * 60 * MINUTE_MS;
  const nowMin = Math.floor(nowMs / MINUTE_MS) * MINUTE_MS;
  const earliest = nowMin - maxLookback;
  let startMin = lastRunAtMs == null
    ? nowMin
    : Math.max(Math.floor(lastRunAtMs / MINUTE_MS) * MINUTE_MS + MINUTE_MS, earliest);
  for (let t = startMin; t <= nowMin; t += MINUTE_MS) {
    if (matchesAt(c, new Date(t))) return true;
  }
  return false;
}

export function validateSchedule(
  cfg: unknown,
  opts: { projectRepos?: string[]; allowedProviders?: string[]; knownProbes?: string[] } = {},
): string[] {
  const errors: string[] = [];
  if (cfg === null || typeof cfg !== 'object' || Array.isArray(cfg)) {
    return ['schedule must be an object'];
  }
  const s = cfg as Record<string, unknown>;

  if (!s.name || typeof s.name !== 'string' || !s.name.trim()) errors.push('name is required');

  if (!TASK_TYPES.includes(s.task_type as TaskType)) {
    errors.push(`task_type must be one of ${TASK_TYPES.join(' | ')}`);
  }

  // A drift schedule is defined by its probes, not a prompt. A prompt is still
  // ALLOWED (operator context on the filed issue) but never required.
  const isDrift = s.task_type === DRIFT_TASK_TYPE;
  if (!isDrift || (s.prompt !== undefined && s.prompt !== '')) {
    if (!s.prompt || typeof s.prompt !== 'string' || s.prompt.trim().length < MIN_PROMPT_LEN) {
      errors.push(`prompt is required (at least ${MIN_PROMPT_LEN} characters)`);
    } else if (s.prompt.length > MAX_PROMPT_LEN) {
      errors.push(`prompt must be at most ${MAX_PROMPT_LEN} characters`);
    }
  }

  if (isDrift) {
    if (!Array.isArray(s.probes) || s.probes.length === 0) {
      errors.push('at least one probe is required for a drift schedule');
    } else if (s.probes.some((p) => typeof p !== 'string' || !/^[a-z0-9][a-z0-9-]{0,47}[a-z0-9]$/.test(p))) {
      errors.push('every probe must be a lowercase kebab-case id');
    } else {
      const dupes = (s.probes as string[]).filter((p, i) => (s.probes as string[]).indexOf(p) !== i);
      if (dupes.length) {
        errors.push(`duplicate probes: ${[...new Set(dupes)].join(', ')} — a probe would run twice and diff against its own output`);
      }
      if (opts.knownProbes) {
        const unknown = (s.probes as string[]).filter((p) => !opts.knownProbes!.includes(p));
        if (unknown.length) {
          errors.push(`unknown probe(s): ${unknown.join(', ')}. Available: ${opts.knownProbes!.join(', ') || 'none registered'}`);
        }
      }
    }
  } else if (s.probes !== undefined) {
    errors.push(`probes are only valid on a "${DRIFT_TASK_TYPE}" schedule`);
  }

  if (!Array.isArray(s.repos) || s.repos.length === 0) {
    errors.push('at least one target repo is required');
  } else {
    if (s.repos.some((r) => typeof r !== 'string' || !/^[^/\s]+\/[^/\s]+$/.test(r))) {
      errors.push('every repo must be an "org/repo" string');
    }
    if (opts.projectRepos) {
      const allowed = new Set(opts.projectRepos);
      const stray = (s.repos as string[]).filter((r) => !allowed.has(r));
      if (stray.length) errors.push(`these repos are not attached to the project: ${stray.join(', ')}`);
    }
  }

  if (typeof s.cron !== 'string' || !isValidCron(s.cron)) {
    errors.push('cron must be a valid 5-field expression (e.g. "0 2 * * 1" = Mondays 02:00 UTC)');
  }

  if (s.enabled !== undefined && typeof s.enabled !== 'boolean') errors.push('enabled must be a boolean');

  if (s.provider !== undefined && s.provider !== '') {
    if (typeof s.provider !== 'string' || !(SCHEDULE_PROVIDERS as readonly string[]).includes(s.provider)) {
      errors.push(`provider must be one of ${SCHEDULE_PROVIDERS.join(' | ')}`);
    } else if (opts.allowedProviders && !opts.allowedProviders.includes(s.provider)) {
      errors.push(`provider "${s.provider}" is not available on this project's plan`);
    }
    if (!s.model || typeof s.model !== 'string' || !s.model.trim()) {
      errors.push('a model is required when a provider is selected');
    }
  }

  return errors;
}
